import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Tooltip, CircularProgress, Alert, TablePagination, TextField, MenuItem, Grid, Button } from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import ReplayIcon from '@mui/icons-material/Replay';
import { NotificationService } from '../../../api/services/notificationService';
import LogPreviewModal from '../../../components/LogPreviewModal';
import { extractTotalCount, standardFilterInputSx, standardFilterSelectSx, standardResetButtonSx } from '../../../components/common/tableStyles';

export default function SystemAuditLogTab({ setNotification, isActive }) {
    const [historyLogs, setHistoryLogs] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);

    const [historyPage, setHistoryPage] = useState(0);
    const [historyRowsPerPage, setHistoryRowsPerPage] = useState(20);
    const [totalLogs, setTotalLogs] = useState(0);

    const [filters, setFilters] = useState({ recipient: '', category: '', channel: '', deliveryStatus: '', startDate: '', endDate: '' });

    const [retryingId, setRetryingId] = useState(null);

    const [logModalOpen, setLogModalOpen] = useState(false);
    const [logToPreview, setLogToPreview] = useState(null);

    useEffect(() => {
        if (isActive) {
            fetchNotificationHistory();
        }
    }, [isActive, historyPage, historyRowsPerPage, filters]);

    const fetchNotificationHistory = async () => {
        setHistoryLoading(true);
        setHistoryError(null);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const params = {
                page: historyPage,
                limit: historyRowsPerPage,
                recipient: filters.recipient || undefined,
                category: filters.category || undefined,
                channel: filters.channel || undefined,
                deliveryStatus: filters.deliveryStatus || undefined,
                startDate: filters.startDate ? `${filters.startDate}T00:00:00.000Z` : undefined,
                endDate: filters.endDate ? `${filters.endDate}T23:59:59.999Z` : undefined
            };
            const data = await NotificationService.getNotificationHistory(params, tenantId);
            const logsArray = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
            setHistoryLogs(logsArray);
            setTotalLogs(extractTotalCount(data, logsArray));
        } catch (err) {
            console.error('Failed to fetch history:', err);
            setHistoryError('Failed to load system audit logs.');
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleHistoryChangePage = (event, newPage) => setHistoryPage(newPage);
    const handleHistoryChangeRowsPerPage = (event) => {
        setHistoryRowsPerPage(parseInt(event.target.value, 10));
        setHistoryPage(0);
    };

    const handlePreviewLog = (log) => {
        setLogToPreview(log);
        setLogModalOpen(true);
    };

    const handleResendLog = async (log) => {
        setRetryingId(log.id);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            await NotificationService.retryNotification(log.id, tenantId);
            setNotification({ open: true, message: 'Notification queued for retry', severity: 'success' });
            await fetchNotificationHistory();
        } catch (err) {
            console.error('Failed to retry notification:', err);
            setNotification({ open: true, message: 'Failed to retry notification', severity: 'error' });
        } finally {
            setRetryingId(null);
        }
    };

    if (historyLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                <CircularProgress />
                <Typography color="text.secondary">Fetching audit logs...</Typography>
            </Box>
        );
    }

    if (historyError) {
        return <Alert severity="error">{historyError}</Alert>;
    }

    const paginatedHistoryLogs = historyLogs;

    const handleClearFilters = () => {
        setFilters({ recipient: '', category: '', channel: '', deliveryStatus: '', startDate: '', endDate: '' });
        setHistoryPage(0);
    };

    const hasFilters = Boolean(filters.recipient || filters.category || filters.channel || filters.deliveryStatus || filters.startDate || filters.endDate);

    return (
        <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: '12px', border: '1px solid #edf1f6', bgcolor: '#ffffff' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            size="small"
                            fullWidth
                            value={filters.recipient}
                            onChange={(e) => { setFilters({ ...filters, recipient: e.target.value }); setHistoryPage(0); }}
                            placeholder="User ID"
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            size="small"
                            fullWidth
                            value={filters.category}
                            onChange={(e) => { setFilters({ ...filters, category: e.target.value }); setHistoryPage(0); }}
                            placeholder="Category (e.g. Alert)"
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            size="small"
                            select
                            fullWidth
                            value={filters.channel}
                            onChange={(e) => { setFilters({ ...filters, channel: e.target.value }); setHistoryPage(0); }}
                            SelectProps={{ displayEmpty: true }}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="">All Channels</MenuItem>
                            <MenuItem value="InApp">In-App</MenuItem>
                            <MenuItem value="Email">Email</MenuItem>
                            <MenuItem value="SMS">SMS</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            size="small"
                            select
                            fullWidth
                            value={filters.deliveryStatus}
                            onChange={(e) => { setFilters({ ...filters, deliveryStatus: e.target.value }); setHistoryPage(0); }}
                            SelectProps={{ displayEmpty: true }}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="Delivered">Delivered</MenuItem>
                            <MenuItem value="Failed">Failed</MenuItem>
                            <MenuItem value="Pending">Pending</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            size="small"
                            type="date"
                            fullWidth
                            value={filters.startDate}
                            onChange={(e) => { setFilters({ ...filters, startDate: e.target.value }); setHistoryPage(0); }}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            size="small"
                            type="date"
                            fullWidth
                            value={filters.endDate}
                            onChange={(e) => { setFilters({ ...filters, endDate: e.target.value }); setHistoryPage(0); }}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    {hasFilters && (
                        <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                            <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={handleClearFilters} >Reset</Button>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Event / Title</TableCell>
                            <TableCell>Channel</TableCell>
                            <TableCell>Recipient</TableCell>
                            <TableCell align="center">Delivery Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedHistoryLogs.length > 0 ? (
                            paginatedHistoryLogs.map((log) => (
                                <TableRow key={log.id} hover>
                                    <TableCell>
                                        <Typography variant="body2">{new Date(log.createdAt).toLocaleDateString()}</Typography>
                                        <Typography variant="caption" color="text.secondary">{new Date(log.createdAt).toLocaleTimeString()}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{log.title}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {log.message}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={log.channel || 'System'} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{log.targetUrl || log.userId || 'System Broadcast'}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={log.errorMessage || 'No errors reported'}>
                                            <Chip 
                                                label={log.deliveryStatus || 'Pending'} 
                                                size="small" 
                                                color={
                                                    log.deliveryStatus === 'Delivered' || log.deliveryStatus === 'Success' ? 'success' : 
                                                    log.deliveryStatus === 'Failed' ? 'error' : 
                                                    'warning'
                                                }
                                                variant="filled"
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View Details">
                                            <IconButton size="small" onClick={() => handlePreviewLog(log)}>
                                                <PreviewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {log.deliveryStatus === 'Failed' && (
                                            <Tooltip title="Retry Delivery">
                                                <IconButton 
                                                    size="small" 
                                                    color="primary" 
                                                    onClick={() => handleResendLog(log)}
                                                    disabled={retryingId === log.id}
                                                >
                                                    {retryingId === log.id ? <CircularProgress size={20} /> : <ReplayIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No history logs found</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={historyLogs.length === 0 ? 0 : totalLogs}
                    rowsPerPage={historyRowsPerPage}
                    page={historyPage}
                    onPageChange={handleHistoryChangePage}
                    onRowsPerPageChange={handleHistoryChangeRowsPerPage}
                />
            </TableContainer>

            <LogPreviewModal
                open={logModalOpen}
                onClose={() => setLogModalOpen(false)}
                log={logToPreview}
            />
        </Box>
    );
}
